import React, { useState, useEffect, useCallback } from 'react';
import examData from './components/mothai/pharma_exam_72.json';

// Color mapping for categories
const categoryColors = {
  'history': '#8b5cf6',
  'herbs': '#10b981',
  'formulas': '#f59e0b',
  'taste': '#ec4899',
  'preparation': '#6366f1'
};

// Transform JSON data to match app structure
const transformExamData = (examData) => {
  // Group questions by category
  const questionsByCategory = {};
  examData.questions.forEach(q => {
    if (!questionsByCategory[q.category]) {
      questionsByCategory[q.category] = [];
    }
    questionsByCategory[q.category].push({
      id: q.id,
      question: q.question,
      options: q.options,
      correct: q.answer, // Convert 'answer' to 'correct'
      explanation: q.explanation,
      difficulty: q.difficulty || 'medium',
      lessonRef: q.lessonRef,
      tags: q.tags || []
    });
  });

  // Build categories array
  const categories = examData.categories.map(cat => ({
    id: cat.id,
    name: cat.name,
    icon: cat.icon,
    color: categoryColors[cat.id] || '#6366f1',
    questions: questionsByCategory[cat.id] || []
  }));

  return {
    examId: examData.examId,
    title: examData.title,
    description: examData.description,
    categories,
    achievements: [
      { id: 'first_quiz', name: 'ก้าวแรก', description: 'ทำแบบทดสอบครั้งแรก', icon: '🎯', xp: 50 },
      { id: 'perfect_score', name: 'เพอร์เฟค!', description: 'ตอบถูกทั้งหมดในหมวดหมู่', icon: '💯', xp: 200 },
      { id: 'speed_demon', name: 'สายฟ้าแลบ', description: 'ตอบถูกภายใน 5 วินาที', icon: '⚡', xp: 100 },
      { id: 'streak_5', name: 'ต่อเนื่อง 5', description: 'ตอบถูกต่อเนื่อง 5 ข้อ', icon: '🔥', xp: 150 },
      { id: 'all_categories', name: 'รอบรู้', description: 'ทำแบบทดสอบครบทุกหมวด', icon: '📚', xp: 300 },
      { id: 'level_5', name: 'มือใหม่หัดเก่ง', description: 'ถึงระดับ 5', icon: '⭐', xp: 100 },
      { id: 'level_10', name: 'นักศึกษาดีเด่น', description: 'ถึงระดับ 10', icon: '🌟', xp: 250 }
    ]
  };
};

const quizData = transformExamData(examData);

// Utility functions
const calculateLevel = (xp) => Math.floor(xp / 100) + 1;
const calculateXPForNextLevel = (level) => level * 100;
const getProgressToNextLevel = (xp) => {
  const level = calculateLevel(xp);
  const currentLevelXP = (level - 1) * 100;
  const nextLevelXP = level * 100;
  return ((xp - currentLevelXP) / (nextLevelXP - currentLevelXP)) * 100;
};

// Components
const ParticleBackground = () => {
  const [particles, setParticles] = useState([]);
  
  useEffect(() => {
    const newParticles = Array.from({ length: 30 }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 100,
      size: Math.random() * 4 + 2,
      duration: Math.random() * 20 + 15,
      delay: Math.random() * 5
    }));
    setParticles(newParticles);
  }, []);
  
  return (
    <div className="particles-container">
      {particles.map(p => (
        <div
          key={p.id}
          className="particle"
          style={{
            left: `${p.x}%`,
            top: `${p.y}%`,
            width: `${p.size}px`,
            height: `${p.size}px`,
            animationDuration: `${p.duration}s`,
            animationDelay: `${p.delay}s`
          }}
        />
      ))}
    </div>
  );
};

// Removed Header component - using Navbar from Layout instead

const CategoryCard = ({ category, stats, onClick }) => {
  const progress = stats ? (stats.correct / stats.total) * 100 : 0;
  
  return (
    <div className="category-card" onClick={onClick} style={{ '--accent': category.color }}>
      <div className="category-icon">{category.icon}</div>
      <h3>{category.name}</h3>
      <div className="category-stats">
        <div className="progress-ring">
          <svg viewBox="0 0 36 36">
            <path
              className="progress-ring-bg"
              d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
            />
            <path
              className="progress-ring-fill"
              strokeDasharray={`${progress}, 100`}
              d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
            />
          </svg>
          <span className="progress-text">{Math.round(progress)}%</span>
        </div>
        <div className="stat-details">
          <span>{stats?.correct || 0}/{stats?.total || category.questions.length} ข้อ</span>
        </div>
      </div>
      <button className="play-btn">เริ่มทำข้อสอบ →</button>
    </div>
  );
};

const QuizScreen = ({ category, onComplete, onBack }) => {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [showResult, setShowResult] = useState(false);
  const [score, setScore] = useState(0);
  const [timeLeft, setTimeLeft] = useState(30);
  const [answers, setAnswers] = useState([]);
  const [isTimerPaused, setIsTimerPaused] = useState(false);
  const [streak, setStreak] = useState(0);
  const [showCombo, setShowCombo] = useState(false);
  
  const question = category.questions[currentQuestion];
  
  useEffect(() => {
    if (isTimerPaused || showResult) return;
    
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          handleAnswer(null);
          return 30;
        }
        return prev - 1;
      });
    }, 1000);
    
    return () => clearInterval(timer);
  }, [currentQuestion, isTimerPaused, showResult]);
  
  const handleAnswer = (index) => {
    if (selectedAnswer !== null) return;
    
    setSelectedAnswer(index);
    setIsTimerPaused(true);
    
    const isCorrect = index === question.correct;
    const newStreak = isCorrect ? streak + 1 : 0;
    setStreak(newStreak);
    
    if (isCorrect) {
      const timeBonus = Math.floor(timeLeft / 3);
      const streakBonus = Math.min(newStreak * 5, 25);
      const basePoints = question.difficulty === 'easy' ? 10 : question.difficulty === 'medium' ? 20 : 30;
      const totalPoints = basePoints + timeBonus + streakBonus;
      setScore(prev => prev + totalPoints);
      
      if (newStreak >= 3) {
        setShowCombo(true);
        setTimeout(() => setShowCombo(false), 1500);
      }
    }
    
    setAnswers([...answers, { questionId: question.id, selected: index, correct: question.correct, isCorrect }]);
    
    setTimeout(() => {
      if (currentQuestion < category.questions.length - 1) {
        setCurrentQuestion(prev => prev + 1);
        setSelectedAnswer(null);
        setTimeLeft(30);
        setIsTimerPaused(false);
      } else {
        setShowResult(true);
      }
    }, 2000);
  };
  
  if (showResult) {
    const correctCount = answers.filter(a => a.isCorrect).length;
    const percentage = Math.round((correctCount / category.questions.length) * 100);
    
    return (
      <div className="result-screen">
        <div className="result-card">
          <div className="result-header">
            <div className={`result-icon ${percentage >= 80 ? 'excellent' : percentage >= 60 ? 'good' : 'try-again'}`}>
              {percentage >= 80 ? '🏆' : percentage >= 60 ? '⭐' : '💪'}
            </div>
            <h2>{percentage >= 80 ? 'ยอดเยี่ยม!' : percentage >= 60 ? 'ดีมาก!' : 'พยายามต่อไป!'}</h2>
          </div>
          
          <div className="result-stats">
            <div className="stat-circle">
              <svg viewBox="0 0 36 36">
                <path
                  className="stat-circle-bg"
                  d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                />
                <path
                  className="stat-circle-fill"
                  strokeDasharray={`${percentage}, 100`}
                  d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                />
              </svg>
              <div className="stat-circle-text">
                <span className="percentage">{percentage}%</span>
                <span className="label">ถูกต้อง</span>
              </div>
            </div>
            
            <div className="result-details">
              <div className="detail-item">
                <span className="icon">✅</span>
                <span className="value">{correctCount}</span>
                <span className="label">ถูก</span>
              </div>
              <div className="detail-item">
                <span className="icon">❌</span>
                <span className="value">{category.questions.length - correctCount}</span>
                <span className="label">ผิด</span>
              </div>
              <div className="detail-item">
                <span className="icon">⭐</span>
                <span className="value">{score}</span>
                <span className="label">XP</span>
              </div>
            </div>
          </div>
          
          <div className="answer-review">
            <h3>สรุปคำตอบ</h3>
            <div className="answer-list">
              {answers.map((answer, idx) => (
                <div key={idx} className={`answer-item ${answer.isCorrect ? 'correct' : 'incorrect'}`}>
                  <span className="answer-num">ข้อ {idx + 1}</span>
                  <span className="answer-icon">{answer.isCorrect ? '✓' : '✗'}</span>
                </div>
              ))}
            </div>
          </div>
          
          <div className="result-actions">
            <button className="btn-secondary" onClick={onBack}>กลับหน้าหลัก</button>
            <button className="btn-primary" onClick={() => {
              setCurrentQuestion(0);
              setSelectedAnswer(null);
              setShowResult(false);
              setScore(0);
              setTimeLeft(30);
              setAnswers([]);
              setStreak(0);
            }}>ทำใหม่อีกครั้ง</button>
          </div>
        </div>
        <button className="btn-complete" onClick={() => onComplete(score, answers)}>บันทึกผลและออก</button>
      </div>
    );
  }
  
  return (
    <div className="quiz-screen" style={{ '--accent': category.color }}>
      <div className="quiz-header">
        <button className="back-btn" onClick={onBack}>← ออก</button>
        <div className="quiz-info">
          <span className="category-label">{category.icon} {category.name}</span>
          <span className="question-counter">ข้อ {currentQuestion + 1}/{category.questions.length}</span>
        </div>
        <div className="score-display">
          <span className="score-icon">⭐</span>
          <span>{score} XP</span>
        </div>
      </div>
      
      <div className="progress-bar">
        <div className="progress-fill" style={{ width: `${((currentQuestion + 1) / category.questions.length) * 100}%` }} />
      </div>
      
      <div className="quiz-content">
        {showCombo && (
          <div className="combo-popup">
            <span className="combo-text">🔥 COMBO x{streak}!</span>
          </div>
        )}
        
        <div className="timer-container">
          <svg className="timer-svg" viewBox="0 0 100 100">
            <circle className="timer-bg" cx="50" cy="50" r="45" />
            <circle 
              className="timer-fill" 
              cx="50" 
              cy="50" 
              r="45"
              style={{ 
                strokeDasharray: `${(timeLeft / 30) * 283} 283`,
                stroke: timeLeft <= 10 ? '#ef4444' : timeLeft <= 20 ? '#f59e0b' : '#10b981'
              }}
            />
          </svg>
          <span className={`timer-text ${timeLeft <= 10 ? 'warning' : ''}`}>{timeLeft}</span>
        </div>
        
        <div className="difficulty-badge" data-difficulty={question.difficulty}>
          {question.difficulty === 'easy' ? '⭐ ง่าย' : question.difficulty === 'medium' ? '⭐⭐ ปานกลาง' : '⭐⭐⭐ ยาก'}
        </div>
        
        <div className="question-card">
          <h2 className="question-text">{question.question}</h2>
          
          <div className="options-grid">
            {question.options.map((option, idx) => (
              <button
                key={idx}
                className={`option-btn ${selectedAnswer === idx ? (idx === question.correct ? 'correct' : 'incorrect') : ''} ${selectedAnswer !== null && idx === question.correct ? 'correct' : ''}`}
                onClick={() => handleAnswer(idx)}
                disabled={selectedAnswer !== null}
              >
                <span className="option-letter">{['ก', 'ข', 'ค', 'ง'][idx]}</span>
                <span className="option-text">{option}</span>
                {selectedAnswer !== null && idx === question.correct && <span className="check-icon">✓</span>}
                {selectedAnswer === idx && idx !== question.correct && <span className="cross-icon">✗</span>}
              </button>
            ))}
          </div>
          
          {selectedAnswer !== null && (
            <div className={`explanation ${selectedAnswer === question.correct ? 'correct' : 'incorrect'}`}>
              <div className="explanation-header">
                {selectedAnswer === question.correct ? '✅ ถูกต้อง!' : '❌ ไม่ถูกต้อง'}
              </div>
              <p>{question.explanation}</p>
            </div>
          )}
        </div>
      </div>
      
      <div className="streak-indicator">
        {Array.from({ length: 5 }, (_, i) => (
          <div key={i} className={`streak-dot ${i < streak ? 'active' : ''}`}>
            {i < streak ? '🔥' : '○'}
          </div>
        ))}
      </div>
    </div>
  );
};

const ProfileModal = ({ user, achievements, onClose }) => {
  const level = calculateLevel(user.xp);
  const unlockedAchievements = achievements.filter(a => user.achievements.includes(a.id));
  
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="profile-modal" onClick={e => e.stopPropagation()}>
        <button className="close-btn" onClick={onClose}>×</button>
        
        <div className="profile-header">
          <div className="avatar">
            <span>👨‍⚕️</span>
            <div className="level-ring">Lv.{level}</div>
          </div>
          <h2>นักศึกษาแพทย์แผนไทย</h2>
        </div>
        
        <div className="stats-grid">
          <div className="stat-card">
            <span className="stat-value">{user.xp}</span>
            <span className="stat-label">XP รวม</span>
          </div>
          <div className="stat-card">
            <span className="stat-value">{user.totalQuizzes}</span>
            <span className="stat-label">ทำแบบทดสอบ</span>
          </div>
          <div className="stat-card">
            <span className="stat-value">{user.streak}</span>
            <span className="stat-label">🔥 Streak</span>
          </div>
          <div className="stat-card">
            <span className="stat-value">{Math.round(user.accuracy)}%</span>
            <span className="stat-label">ความแม่นยำ</span>
          </div>
        </div>
        
        <div className="achievements-section">
          <h3>🏆 ความสำเร็จ ({unlockedAchievements.length}/{achievements.length})</h3>
          <div className="achievements-grid">
            {achievements.map(achievement => {
              const unlocked = user.achievements.includes(achievement.id);
              return (
                <div key={achievement.id} className={`achievement-card ${unlocked ? 'unlocked' : 'locked'}`}>
                  <span className="achievement-icon">{achievement.icon}</span>
                  <div className="achievement-info">
                    <span className="achievement-name">{achievement.name}</span>
                    <span className="achievement-desc">{achievement.description}</span>
                  </div>
                  {unlocked && <span className="achievement-xp">+{achievement.xp} XP</span>}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

const LeaderboardCard = () => {
  const fakeLeaders = [
    { rank: 1, name: 'หมอสมชาย', xp: 2850, level: 29 },
    { rank: 2, name: 'เภสัชหญิงมานี', xp: 2340, level: 24 },
    { rank: 3, name: 'นศพ.วิชัย', xp: 1980, level: 20 },
    { rank: 4, name: 'คุณ (คุณ)', xp: 0, level: 1, isUser: true },
    { rank: 5, name: 'นศ.สุดา', xp: 1540, level: 16 }
  ];
  
  return (
    <div className="leaderboard-card">
      <h3>🏆 กระดานผู้นำ</h3>
      <div className="leaderboard-list">
        {fakeLeaders.map(leader => (
          <div key={leader.rank} className={`leader-item ${leader.isUser ? 'is-user' : ''}`}>
            <span className="rank">
              {leader.rank === 1 ? '🥇' : leader.rank === 2 ? '🥈' : leader.rank === 3 ? '🥉' : `#${leader.rank}`}
            </span>
            <span className="name">{leader.name}</span>
            <span className="level">Lv.{leader.level}</span>
            <span className="xp">{leader.xp.toLocaleString()} XP</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const DailyChallenge = ({ onStart }) => (
  <div className="daily-challenge">
    <div className="challenge-glow" />
    <div className="challenge-content">
      <div className="challenge-icon">⚔️</div>
      <div className="challenge-info">
        <h3>ภารกิจประจำวัน</h3>
        <p>ทำแบบทดสอบรวม 10 ข้อจากทุกหมวด</p>
        <span className="reward">รางวัล: +150 XP</span>
      </div>
      <button className="challenge-btn" onClick={onStart}>เริ่มเลย!</button>
    </div>
  </div>
);

// Main App
export default function MedQuizApp() {
  const [user, setUser] = useState({
    xp: 0,
    streak: 0,
    totalQuizzes: 0,
    accuracy: 0,
    achievements: [],
    categoryStats: {}
  });
  
  const [currentScreen, setCurrentScreen] = useState('home');
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [showProfile, setShowProfile] = useState(false);
  const [notification, setNotification] = useState(null);
  
  const showNotification = useCallback((message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  }, []);
  
  const handleQuizComplete = useCallback((score, answers) => {
    const correctCount = answers.filter(a => a.isCorrect).length;
    const totalAnswered = answers.length;
    
    setUser(prev => {
      const newXP = prev.xp + score;
      const newTotalQuizzes = prev.totalQuizzes + 1;
      const newAccuracy = ((prev.accuracy * prev.totalQuizzes) + (correctCount / totalAnswered * 100)) / newTotalQuizzes;
      const newAchievements = [...prev.achievements];
      
      // Check for new achievements
      if (!prev.achievements.includes('first_quiz')) {
        newAchievements.push('first_quiz');
        showNotification('🎯 ความสำเร็จใหม่: ก้าวแรก!');
      }
      
      if (correctCount === totalAnswered && !prev.achievements.includes('perfect_score')) {
        newAchievements.push('perfect_score');
        showNotification('💯 ความสำเร็จใหม่: เพอร์เฟค!');
      }
      
      const newLevel = calculateLevel(newXP);
      if (newLevel >= 5 && !prev.achievements.includes('level_5')) {
        newAchievements.push('level_5');
        showNotification('⭐ ความสำเร็จใหม่: มือใหม่หัดเก่ง!');
      }
      
      if (newLevel >= 10 && !prev.achievements.includes('level_10')) {
        newAchievements.push('level_10');
        showNotification('🌟 ความสำเร็จใหม่: นักศึกษาดีเด่น!');
      }
      
      const newCategoryStats = { ...prev.categoryStats };
      if (selectedCategory) {
        newCategoryStats[selectedCategory.id] = {
          correct: (newCategoryStats[selectedCategory.id]?.correct || 0) + correctCount,
          total: (newCategoryStats[selectedCategory.id]?.total || 0) + totalAnswered
        };
      }
      
      return {
        ...prev,
        xp: newXP,
        streak: prev.streak + 1,
        totalQuizzes: newTotalQuizzes,
        accuracy: newAccuracy,
        achievements: newAchievements,
        categoryStats: newCategoryStats
      };
    });
    
    setCurrentScreen('home');
    setSelectedCategory(null);
  }, [selectedCategory, showNotification]);
  
  const handleCategorySelect = useCallback((category) => {
    setSelectedCategory(category);
    setCurrentScreen('quiz');
  }, []);
  
  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Kanit:wght@300;400;500;600;700;800;900&family=Sarabun:wght@300;400;500;600;700&display=swap');
        
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }
        
        :root {
          --gold: #D4AF37;
          --gold-light: #F4E4BA;
          --gold-dark: #996515;
          --green-deep: #1A3C34;
          --green-forest: #2D5A4A;
          --green-herb: #4A7C59;
          --green-leaf: #6B9B7A;
          --cream: #FDF8F0;
          --cream-dark: #E8DFD0;
          --white: #FFFFFF;
          --black: #0D1B16;
          
          /* Legacy variables for compatibility */
          --primary: var(--green-deep);
          --primary-dark: var(--green-forest);
          --secondary: var(--green-herb);
          --accent: var(--gold);
          --bg-dark: var(--cream);
          --bg-card: var(--white);
          --bg-card-hover: var(--cream-dark);
          --text: var(--black);
          --text-muted: #666;
          --danger: #ef4444;
          --success: #22c55e;
          --gradient-1: linear-gradient(135deg, var(--green-deep) 0%, var(--green-forest) 100%);
          --gradient-2: linear-gradient(135deg, var(--gold) 0%, var(--gold-dark) 100%);
          --gradient-3: linear-gradient(135deg, var(--green-herb) 0%, var(--green-leaf) 100%);
          --gradient-gold: linear-gradient(135deg, var(--gold) 0%, var(--gold-dark) 100%);
        }
        
        body {
          font-family: 'Sarabun', sans-serif;
          background: var(--cream);
          color: var(--black);
          min-height: 100vh;
          overflow-x: hidden;
          font-weight: 400;
          letter-spacing: 0.01em;
          -webkit-font-smoothing: antialiased;
          -moz-osx-font-smoothing: grayscale;
        }
        
        /* Improve Thai text readability */
        h1, h2, h3, h4, h5, h6 {
          font-family: 'Kanit', sans-serif;
          font-weight: 600;
          letter-spacing: 0.02em;
          line-height: 1.4;
          color: var(--green-deep);
        }
        
        p, span, div, button, a {
          font-family: 'Sarabun', sans-serif;
          line-height: 1.6;
          letter-spacing: 0.01em;
        }
        
        .app-container {
          min-height: 100vh;
          position: relative;
        }
        
        /* Particle Background */
        .particles-container {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          pointer-events: none;
          z-index: 0;
          overflow: hidden;
        }
        
        .particle {
          position: absolute;
          background: radial-gradient(circle, rgba(99, 102, 241, 0.6) 0%, transparent 70%);
          border-radius: 50%;
          animation: float linear infinite;
        }
        
        @keyframes float {
          0% {
            transform: translateY(100vh) rotate(0deg);
            opacity: 0;
          }
          10% {
            opacity: 0.6;
          }
          90% {
            opacity: 0.6;
          }
          100% {
            transform: translateY(-100vh) rotate(720deg);
            opacity: 0;
          }
        }
        
        /* Removed Header styles - using Navbar from Layout instead */
        
        /* Main Content */
        .main-content {
          max-width: 1400px;
          margin: 0 auto;
          padding: 2rem;
          position: relative;
          z-index: 1;
        }
        
        .welcome-section {
          text-align: center;
          margin-bottom: 3rem;
        }
        
        .welcome-section h2 {
          font-size: 2.5rem;
          font-weight: 600;
          margin-bottom: 0.5rem;
          line-height: 1.5;
          letter-spacing: 0.02em;
        }
        
        .welcome-section p {
          color: var(--text-muted);
          font-size: 1.15rem;
          line-height: 1.7;
          letter-spacing: 0.01em;
        }
        
        /* Daily Challenge */
        .daily-challenge {
          position: relative;
          background: linear-gradient(135deg, rgba(212, 175, 55, 0.15) 0%, rgba(26, 60, 52, 0.2) 100%);
          border: 1px solid rgba(212, 175, 55, 0.3);
          border-radius: 24px;
          padding: 2rem;
          margin-bottom: 3rem;
          overflow: hidden;
          box-shadow: 0 8px 30px rgba(0, 0, 0, 0.1);
        }
        
        .challenge-glow {
          position: absolute;
          top: -50%;
          left: -50%;
          width: 200%;
          height: 200%;
          background: radial-gradient(circle, rgba(212, 175, 55, 0.2) 0%, transparent 50%);
          animation: rotate 10s linear infinite;
        }
        
        @keyframes rotate {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        
        .challenge-content {
          position: relative;
          display: flex;
          align-items: center;
          gap: 2rem;
        }
        
        .challenge-icon {
          font-size: 4rem;
          animation: bounce 2s ease-in-out infinite;
        }
        
        @keyframes bounce {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-10px); }
        }
        
        .challenge-info {
          flex: 1;
        }
        
        .challenge-info h3 {
          font-size: 1.6rem;
          font-weight: 600;
          margin-bottom: 0.5rem;
          line-height: 1.5;
          letter-spacing: 0.02em;
        }
        
        .challenge-info p {
          color: var(--text-muted);
          margin-bottom: 0.5rem;
          font-size: 1.05rem;
          line-height: 1.7;
          letter-spacing: 0.01em;
        }
        
        .reward {
          display: inline-block;
          background: var(--gradient-gold);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          font-weight: 700;
          font-size: 1.1rem;
        }
        
        .challenge-btn {
          background: var(--gradient-gold);
          border: none;
          padding: 1rem 2rem;
          border-radius: 50px;
          font-family: 'Kanit', sans-serif;
          font-size: 1.1rem;
          font-weight: 700;
          color: white;
          cursor: pointer;
          transition: all 0.3s ease;
        }
        
        .challenge-btn:hover {
          transform: scale(1.05);
          box-shadow: 0 10px 40px rgba(245, 175, 25, 0.4);
        }
        
        /* Categories Grid */
        .section-title {
          font-size: 1.6rem;
          font-weight: 600;
          margin-bottom: 1.5rem;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          line-height: 1.5;
          letter-spacing: 0.02em;
        }
        
        .categories-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
          gap: 1.5rem;
          margin-bottom: 3rem;
        }
        
        .category-card {
          background: var(--white);
          border: 1px solid rgba(0, 0, 0, 0.05);
          border-radius: 24px;
          padding: 2rem;
          cursor: pointer;
          transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
          position: relative;
          overflow: hidden;
          box-shadow: 0 4px 15px rgba(0, 0, 0, 0.05);
        }
        
        .category-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 4px;
          background: linear-gradient(90deg, var(--gold) 0%, var(--green-herb) 100%);
          transform: scaleX(0);
          transition: transform 0.3s ease;
        }
        
        .category-card:hover {
          transform: translateY(-10px);
          border-color: var(--gold);
          box-shadow: 0 25px 50px rgba(0, 0, 0, 0.1);
        }
        
        .category-card:hover::before {
          transform: scaleX(1);
        }
        
        .category-icon {
          font-size: 3rem;
          margin-bottom: 1rem;
          display: block;
        }
        
        .category-card h3 {
          font-size: 1.35rem;
          font-weight: 600;
          margin-bottom: 1rem;
          line-height: 1.5;
          letter-spacing: 0.02em;
        }
        
        .category-stats {
          display: flex;
          align-items: center;
          gap: 1rem;
          margin-bottom: 1.5rem;
        }
        
        .progress-ring {
          width: 60px;
          height: 60px;
          position: relative;
        }
        
        .progress-ring svg {
          transform: rotate(-90deg);
          width: 100%;
          height: 100%;
        }
        
        .progress-ring-bg {
          fill: none;
          stroke: rgba(0, 0, 0, 0.1);
          stroke-width: 3;
        }
        
        .progress-ring-fill {
          fill: none;
          stroke: var(--gold);
          stroke-width: 3;
          stroke-linecap: round;
          transition: stroke-dasharray 0.5s ease;
        }
        
        .progress-text {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          font-size: 0.85rem;
          font-weight: 700;
          color: var(--green-deep);
        }
        
        .stat-details {
          color: var(--text-muted);
          font-size: 0.9rem;
        }
        
        .play-btn {
          width: 100%;
          background: transparent;
          border: 2px solid var(--gold);
          color: var(--gold-dark);
          padding: 0.8rem;
          border-radius: 50px;
          font-family: 'Kanit', sans-serif;
          font-size: 1rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
        }
        
        .play-btn:hover {
          background: var(--gradient-gold);
          color: var(--black);
          box-shadow: 0 4px 15px rgba(212, 175, 55, 0.4);
          transform: translateY(-2px);
        }
        
        /* Leaderboard */
        .sidebar-section {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: 1.5rem;
        }
        
        .leaderboard-card {
          background: var(--white);
          border: 1px solid rgba(0, 0, 0, 0.05);
          border-radius: 24px;
          padding: 1.5rem;
        }
        
        .leaderboard-card h3 {
          font-size: 1.2rem;
          margin-bottom: 1rem;
        }
        
        .leaderboard-list {
          display: flex;
          flex-direction: column;
          gap: 0.8rem;
        }
        
        .leader-item {
          display: flex;
          align-items: center;
          gap: 1rem;
          padding: 0.8rem;
          background: var(--cream);
          border-radius: 16px;
          transition: all 0.3s ease;
        }
        
        .leader-item:hover {
          background: var(--cream-dark);
        }
        
        .leader-item.is-user {
          background: rgba(212, 175, 55, 0.15);
          border: 1px solid rgba(212, 175, 55, 0.3);
        }
        
        .leader-item .rank {
          font-size: 1.2rem;
          min-width: 40px;
        }
        
        .leader-item .name {
          flex: 1;
          font-weight: 500;
        }
        
        .leader-item .level {
          color: var(--text-muted);
          font-size: 0.85rem;
        }
        
        .leader-item .xp {
          font-weight: 700;
          color: var(--gold-dark);
        }
        
        /* Quiz Screen */
        .quiz-screen {
          min-height: 100vh;
          background: var(--cream);
          position: relative;
        }
        
        .quiz-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 1.5rem 2rem;
          background: rgba(26, 60, 52, 0.95);
          backdrop-filter: blur(20px);
          position: sticky;
          top: 0;
          z-index: 10;
        }
        
        .back-btn {
          background: rgba(0, 0, 0, 0.05);
          border: none;
          color: var(--black);
          padding: 0.8rem 1.5rem;
          border-radius: 50px;
          font-family: 'Kanit', sans-serif;
          font-size: 1rem;
          cursor: pointer;
          transition: all 0.3s ease;
        }
        
        .back-btn:hover {
          background: rgba(212, 175, 55, 0.2);
        }
        
        .quiz-info {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 0.3rem;
        }
        
        .category-label {
          font-size: 0.9rem;
          color: var(--text-muted);
        }
        
        .question-counter {
          font-size: 1.2rem;
          font-weight: 700;
          color: var(--white);
        }
        
        .score-display {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          background: var(--gradient-gold);
          color: var(--black);
          padding: 0.8rem 1.5rem;
          border-radius: 50px;
          font-weight: 700;
          box-shadow: 0 4px 15px rgba(212, 175, 55, 0.3);
        }
        
        .score-icon {
          font-size: 1.2rem;
        }
        
        .progress-bar {
          height: 6px;
          background: rgba(0, 0, 0, 0.1);
        }
        
        .progress-fill {
          height: 100%;
          background: var(--gradient-gold);
          transition: width 0.5s ease;
        }
        
        .quiz-content {
          max-width: 800px;
          margin: 0 auto;
          padding: 2rem;
          position: relative;
        }
        
        .combo-popup {
          position: fixed;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          z-index: 100;
          animation: comboPopup 1.5s ease forwards;
        }
        
        .combo-text {
          font-size: 3rem;
          font-weight: 800;
          background: var(--gradient-gold);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          text-shadow: 0 0 40px rgba(245, 175, 25, 0.5);
        }
        
        @keyframes comboPopup {
          0% { transform: translate(-50%, -50%) scale(0); opacity: 0; }
          50% { transform: translate(-50%, -50%) scale(1.2); opacity: 1; }
          100% { transform: translate(-50%, -50%) scale(1); opacity: 0; }
        }
        
        .timer-container {
          width: 100px;
          height: 100px;
          margin: 0 auto 1.5rem;
          position: relative;
        }
        
        .timer-svg {
          width: 100%;
          height: 100%;
          transform: rotate(-90deg);
        }
        
        .timer-bg {
          fill: none;
          stroke: rgba(0, 0, 0, 0.1);
          stroke-width: 6;
        }
        
        .timer-fill {
          fill: none;
          stroke-width: 6;
          stroke-linecap: round;
          transition: stroke-dasharray 1s linear, stroke 0.3s ease;
          stroke: var(--gold);
        }
        
        .timer-text {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          font-size: 2rem;
          font-weight: 700;
          color: var(--green-deep);
        }
        
        .timer-text.warning {
          color: var(--danger);
          animation: timerPulse 0.5s ease infinite;
        }
        
        @keyframes timerPulse {
          0%, 100% { transform: translate(-50%, -50%) scale(1); }
          50% { transform: translate(-50%, -50%) scale(1.1); }
        }
        
        .difficulty-badge {
          text-align: center;
          margin-bottom: 1.5rem;
        }
        
        .difficulty-badge[data-difficulty="easy"] {
          color: #22c55e;
        }
        
        .difficulty-badge[data-difficulty="medium"] {
          color: #f59e0b;
        }
        
        .difficulty-badge[data-difficulty="hard"] {
          color: #ef4444;
        }
        
        .question-card {
          background: var(--white);
          border: 1px solid rgba(0, 0, 0, 0.05);
          border-radius: 24px;
          padding: 2rem;
        }
        
        .question-text {
          font-size: 1.5rem;
          font-weight: 500;
          line-height: 1.8;
          margin-bottom: 2rem;
          text-align: center;
          letter-spacing: 0.01em;
          color: var(--black);
        }
        
        .options-grid {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }
        
        .option-btn {
          display: flex;
          align-items: center;
          gap: 1rem;
          width: 100%;
          background: var(--cream);
          border: 2px solid rgba(0, 0, 0, 0.05);
          border-radius: 16px;
          padding: 1.2rem 1.5rem;
          color: var(--black);
          font-family: 'Kanit', sans-serif;
          font-size: 1.05rem;
          text-align: left;
          cursor: pointer;
          transition: all 0.3s ease;
          position: relative;
          line-height: 1.6;
          letter-spacing: 0.01em;
        }
        
        .option-btn:hover:not(:disabled) {
          background: var(--cream-dark);
          border-color: var(--gold);
          transform: translateX(8px);
        }
        
        .option-btn:disabled {
          cursor: default;
        }
        
        .option-btn.correct {
          background: rgba(34, 197, 94, 0.2);
          border-color: var(--success);
        }
        
        .option-btn.incorrect {
          background: rgba(239, 68, 68, 0.2);
          border-color: var(--danger);
        }
        
        .option-letter {
          width: 40px;
          height: 40px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: var(--gradient-gold);
          color: var(--black);
          border-radius: 16px;
          font-weight: 700;
          font-size: 1.1rem;
          flex-shrink: 0;
        }
        
        .option-text {
          flex: 1;
          font-size: 1.05rem;
          line-height: 1.6;
          letter-spacing: 0.01em;
        }
        
        .check-icon, .cross-icon {
          font-size: 1.5rem;
          font-weight: 700;
        }
        
        .check-icon {
          color: var(--success);
        }
        
        .cross-icon {
          color: var(--danger);
        }
        
        .explanation {
          margin-top: 1.5rem;
          padding: 1.5rem;
          border-radius: 16px;
          animation: slideIn 0.3s ease;
        }
        
        @keyframes slideIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        
        .explanation.correct {
          background: rgba(34, 197, 94, 0.1);
          border: 1px solid rgba(34, 197, 94, 0.3);
        }
        
        .explanation.incorrect {
          background: rgba(239, 68, 68, 0.1);
          border: 1px solid rgba(239, 68, 68, 0.3);
        }
        
        .explanation-header {
          font-size: 1.25rem;
          font-weight: 600;
          margin-bottom: 0.8rem;
          line-height: 1.5;
          letter-spacing: 0.02em;
        }
        
        .explanation p {
          color: var(--text-muted);
          line-height: 1.7;
          font-size: 1.05rem;
          letter-spacing: 0.01em;
        }
        
        .streak-indicator {
          display: flex;
          justify-content: center;
          gap: 0.8rem;
          margin-top: 2rem;
        }
        
        .streak-dot {
          width: 40px;
          height: 40px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(0, 0, 0, 0.05);
          border-radius: 50%;
          font-size: 1.2rem;
          transition: all 0.3s ease;
        }
        
        .streak-dot.active {
          background: var(--gradient-gold);
          color: var(--black);
          animation: streakPop 0.3s ease;
        }
        
        @keyframes streakPop {
          0% { transform: scale(0); }
          50% { transform: scale(1.3); }
          100% { transform: scale(1); }
        }
        
        /* Result Screen */
        .result-screen {
          min-height: 100vh;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 2rem;
        }
        
        .result-card {
          background: var(--white);
          border: 1px solid rgba(0, 0, 0, 0.05);
          border-radius: 32px;
          padding: 3rem;
          max-width: 600px;
          width: 100%;
          text-align: center;
        }
        
        .result-header {
          margin-bottom: 2rem;
        }
        
        .result-icon {
          font-size: 5rem;
          display: block;
          margin-bottom: 1rem;
          animation: resultPop 0.5s ease;
        }
        
        @keyframes resultPop {
          0% { transform: scale(0) rotate(-180deg); }
          100% { transform: scale(1) rotate(0); }
        }
        
        .result-header h2 {
          font-size: 2rem;
          font-weight: 800;
        }
        
        .result-stats {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 2rem;
          margin-bottom: 2rem;
        }
        
        .stat-circle {
          width: 150px;
          height: 150px;
          position: relative;
        }
        
        .stat-circle svg {
          width: 100%;
          height: 100%;
          transform: rotate(-90deg);
        }
        
        .stat-circle-bg {
          fill: none;
          stroke: rgba(0, 0, 0, 0.1);
          stroke-width: 8;
        }
        
        .stat-circle-fill {
          fill: none;
          stroke: var(--gold);
          stroke-width: 8;
          stroke-linecap: round;
          animation: circleGrow 1s ease forwards;
        }
        
        @keyframes circleGrow {
          from { stroke-dasharray: 0, 100; }
        }
        
        .stat-circle-text {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          text-align: center;
        }
        
        .stat-circle-text .percentage {
          display: block;
          font-size: 2.5rem;
          font-weight: 800;
          color: var(--green-deep);
        }
        
        .stat-circle-text .label {
          color: var(--text-muted);
          font-size: 0.9rem;
        }
        
        .result-details {
          display: flex;
          justify-content: center;
          gap: 3rem;
        }
        
        .detail-item {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 0.3rem;
        }
        
        .detail-item .icon {
          font-size: 1.5rem;
        }
        
        .detail-item .value {
          font-size: 1.8rem;
          font-weight: 700;
        }
        
        .detail-item .label {
          color: var(--text-muted);
          font-size: 0.85rem;
        }
        
        .answer-review {
          margin-bottom: 2rem;
          text-align: left;
        }
        
        .answer-review h3 {
          margin-bottom: 1rem;
          font-size: 1.1rem;
        }
        
        .answer-list {
          display: flex;
          flex-wrap: wrap;
          gap: 0.5rem;
        }
        
        .answer-item {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.5rem 1rem;
          border-radius: 50px;
          font-size: 0.9rem;
        }
        
        .answer-item.correct {
          background: rgba(34, 197, 94, 0.2);
          color: var(--success);
        }
        
        .answer-item.incorrect {
          background: rgba(239, 68, 68, 0.2);
          color: var(--danger);
        }
        
        .result-actions {
          display: flex;
          gap: 1rem;
          justify-content: center;
          margin-bottom: 1rem;
        }
        
        .btn-primary, .btn-secondary {
          padding: 1rem 2rem;
          border-radius: 50px;
          font-family: 'Kanit', sans-serif;
          font-size: 1rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
        }
        
        .btn-primary {
          background: var(--gradient-gold);
          border: none;
          color: var(--black);
          box-shadow: 0 8px 30px rgba(212, 175, 55, 0.4);
        }
        
        .btn-primary:hover {
          transform: translateY(-3px);
          box-shadow: 0 12px 40px rgba(212, 175, 55, 0.5);
        }
        
        .btn-secondary {
          background: rgba(0, 0, 0, 0.05);
          border: 2px solid rgba(212, 175, 55, 0.3);
          color: var(--green-deep);
          backdrop-filter: blur(10px);
        }
        
        .btn-secondary:hover {
          background: rgba(212, 175, 55, 0.2);
          border-color: var(--gold);
        }
        
        .btn-complete {
          margin-top: 1rem;
          background: var(--gradient-gold);
          border: none;
          padding: 1rem 3rem;
          border-radius: 50px;
          font-family: 'Kanit', sans-serif;
          font-size: 1.1rem;
          font-weight: 700;
          color: var(--black);
          cursor: pointer;
          transition: all 0.3s ease;
          box-shadow: 0 8px 30px rgba(212, 175, 55, 0.4);
        }
        
        .btn-complete:hover {
          transform: translateY(-3px);
          box-shadow: 0 12px 40px rgba(212, 175, 55, 0.5);
        }
        
        /* Profile Modal */
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.8);
          backdrop-filter: blur(10px);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          padding: 2rem;
          animation: fadeIn 0.3s ease;
        }
        
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        
        .profile-modal {
          background: var(--white);
          border: 1px solid rgba(0, 0, 0, 0.05);
          border-radius: 32px;
          padding: 2rem;
          max-width: 600px;
          width: 100%;
          max-height: 90vh;
          overflow-y: auto;
          position: relative;
          animation: slideUp 0.3s ease;
        }
        
        @keyframes slideUp {
          from { transform: translateY(50px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        
        .close-btn {
          position: absolute;
          top: 1.5rem;
          right: 1.5rem;
          width: 40px;
          height: 40px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(0, 0, 0, 0.05);
          border: none;
          border-radius: 50%;
          color: var(--black);
          font-size: 1.5rem;
          cursor: pointer;
          transition: all 0.3s ease;
        }
        
        .close-btn:hover {
          background: rgba(0, 0, 0, 0.1);
        }
        
        .profile-header {
          text-align: center;
          margin-bottom: 2rem;
        }
        
        .avatar {
          position: relative;
          width: 100px;
          height: 100px;
          margin: 0 auto 1rem;
        }
        
        .avatar span {
          font-size: 4rem;
        }
        
        .level-ring {
          position: absolute;
          bottom: -5px;
          right: -5px;
          background: var(--gradient-gold);
          color: var(--black);
          padding: 0.3rem 0.8rem;
          border-radius: 50px;
          font-size: 0.8rem;
          font-weight: 700;
        }
        
        .profile-header h2 {
          font-size: 1.5rem;
          font-weight: 700;
        }
        
        .stats-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 1rem;
          margin-bottom: 2rem;
        }
        
        .stat-card {
          background: var(--cream);
          border-radius: 16px;
          padding: 1.5rem;
          text-align: center;
        }
        
        .stat-card .stat-value {
          display: block;
          font-size: 2rem;
          font-weight: 800;
          background: var(--gradient-gold);
          color: var(--black);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
        
        .stat-card .stat-label {
          color: var(--text-muted);
          font-size: 0.9rem;
        }
        
        .achievements-section h3 {
          margin-bottom: 1rem;
        }
        
        .achievements-grid {
          display: flex;
          flex-direction: column;
          gap: 0.8rem;
        }
        
        .achievement-card {
          display: flex;
          align-items: center;
          gap: 1rem;
          padding: 1rem;
          background: var(--cream);
          border-radius: 16px;
          transition: all 0.3s ease;
        }
        
        .achievement-card.locked {
          opacity: 0.5;
          filter: grayscale(1);
        }
        
        .achievement-card.unlocked {
          background: rgba(99, 102, 241, 0.1);
          border: 1px solid rgba(99, 102, 241, 0.3);
        }
        
        .achievement-icon {
          font-size: 2rem;
        }
        
        .achievement-info {
          flex: 1;
        }
        
        .achievement-name {
          display: block;
          font-weight: 600;
          color: var(--green-deep);
        }
        
        .achievement-desc {
          color: var(--text-muted);
          font-size: 0.85rem;
        }
        
        .achievement-xp {
          color: var(--gold-dark);
          font-weight: 700;
        }
        
        /* Notification */
        .notification {
          position: fixed;
          top: 100px;
          left: 50%;
          transform: translateX(-50%);
          background: var(--bg-card);
          border: 1px solid var(--primary);
          border-radius: 50px;
          padding: 1rem 2rem;
          z-index: 1001;
          animation: notificationSlide 0.3s ease;
          box-shadow: 0 10px 40px rgba(99, 102, 241, 0.3);
        }
        
        @keyframes notificationSlide {
          from { transform: translateX(-50%) translateY(-50px); opacity: 0; }
          to { transform: translateX(-50%) translateY(0); opacity: 1; }
        }
        
        /* Responsive */
        @media (max-width: 768px) {
          /* Removed header responsive styles */
          
          .challenge-content {
            flex-direction: column;
            text-align: center;
          }
          
          .categories-grid {
            grid-template-columns: 1fr;
          }
          
          .result-details {
            gap: 1.5rem;
          }
          
          .result-actions {
            flex-direction: column;
          }
          
          .stats-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
      
      <div className="app-container">
        <ParticleBackground />
        
        {notification && (
          <div className="notification">
            {notification.message}
          </div>
        )}
        
        {currentScreen === 'home' ? (
          <>
            <main className="main-content" style={{ paddingTop: '1rem' }}>
              <section className="welcome-section">
                <h2>{quizData.title || '🌿 ยินดีต้อนรับ นักศึกษา!'}</h2>
                <p>{quizData.description || 'เตรียมพร้อมสู่การสอบแพทย์แผนไทยและเภสัชกรรม'}</p>
              </section>
              
              <DailyChallenge onStart={() => handleCategorySelect(quizData.categories[0])} />
              
              <section>
                <h3 className="section-title">📚 หมวดหมู่ข้อสอบ</h3>
                <div className="categories-grid">
                  {quizData.categories
                    .filter(category => category.questions && category.questions.length > 0)
                    .map(category => (
                      <CategoryCard
                        key={category.id}
                        category={category}
                        stats={user.categoryStats[category.id]}
                        onClick={() => handleCategorySelect(category)}
                      />
                    ))}
                </div>
              </section>
              
              <section className="sidebar-section">
                <LeaderboardCard />
              </section>
            </main>
            
            {showProfile && (
              <ProfileModal
                user={user}
                achievements={quizData.achievements}
                onClose={() => setShowProfile(false)}
              />
            )}
          </>
        ) : (
          <QuizScreen
            category={selectedCategory}
            onComplete={handleQuizComplete}
            onBack={() => {
              setCurrentScreen('home');
              setSelectedCategory(null);
            }}
          />
        )}
      </div>
    </>
  );
}